import { useState, useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet-async'

// Interface for partner logo objects
export interface PartnerLogo {
  src: string
  alt: string
  width?: number // Width in pixels for desktop view
}

// Infinite carousel component props
export interface InfiniteCarouselProps {
  logos: PartnerLogo[]
  defaultLogoWidth?: number // Default width if not specified per logo
}

// Reusable infinite carousel component
export function InfiniteCarousel({ logos, defaultLogoWidth = 180 }: InfiniteCarouselProps) {
  const [isMobile, setIsMobile] = useState(false)
  const uniqueId = useRef(`carousel-${Math.random().toString(36).substr(2, 9)}`).current

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Create an array with enough repetitions to ensure smooth scrolling
  const duplicatedLogos = [...logos, ...logos, ...logos, ...logos]

  // Calculate total width of all logos combined for animation
  const getTotalWidth = () => {
    return logos.reduce((total, logo) => total + (logo.width || defaultLogoWidth), 0)
  }

  // Animation duration based on total width (slower with larger logos)
  const animationDuration = getTotalWidth() / 50 // Adjust speed factor as needed

  // Dynamically create the keyframes CSS
  useEffect(() => {
    const totalWidth = getTotalWidth()

    const styleTag = document.createElement('style')
    styleTag.innerHTML = `
      @keyframes scroll-${uniqueId} {
        0% {
          transform: translateX(0);
        }
        100% {
          transform: translateX(-${totalWidth}px);
        }
      }
    `
    document.head.appendChild(styleTag)

    return () => {
      if (document.head.contains(styleTag)) {
        document.head.removeChild(styleTag)
      }
    }
  }, [logos, uniqueId])

  const containerStyle = {
    width: '100%',
    overflow: 'hidden',
    position: 'relative' as 'relative',
    padding: '20px 0'
  }

  const trackStyle = {
    display: 'flex',
    width: 'fit-content',
    animation: `scroll-${uniqueId} ${animationDuration}s linear infinite`
  }

  const itemStyle = {
    flex: '0 0 auto',
    padding: '0 15px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }

  // Get the appropriate logo width based on device and custom settings
  const getLogoStyle = (logo: PartnerLogo) => {
    // Use logo's custom width or default width
    const baseWidth = logo.width || defaultLogoWidth

    // Scale down on mobile (by 60%)
    const scaleFactor = isMobile ? 0.6 : 1
    const finalWidth = baseWidth * scaleFactor

    return {
      width: `${finalWidth}px`,
      height: 'auto',
      objectFit: 'contain' as 'contain',
    }
  }

  return (
    <div style={containerStyle}>
      {/* Preload images for better performance */}
      <Helmet>
        {logos.map((logo, index) => (
          <link key={`preload-${index}`} rel="preload" href={logo.src} as="image" />
        ))}
      </Helmet>
      <div style={trackStyle}>
        {duplicatedLogos.map((logo, index) => (
          <div key={`logo-${index}`} style={itemStyle}>
            <img
              src={logo.src}
              alt={logo.alt}
              style={getLogoStyle(logo)}
            />
          </div>
        ))}
      </div>
    </div>
  )
} 